import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { hasValidSession, clearSession } from './utils/auth';

// ─── Lazy-loaded pages ────────────────────────────────────────────────────────
const LoginPage        = lazy(() => import('./components/Login'));
const Dashboard        = lazy(() => import('./pages/Dashboard/Dashboard'));
const VerificationForm = lazy(() => import('./pages/VerificationForm'));
const Expired          = lazy(() => import('./pages/Expired'));
const StartVerificationForm = lazy(() => import('./pages/StartVerificationForm')); 
const RequestPermissionForm = lazy(() => import('./pages/RequestPermissionForm'));
const ResetPasswordPage = lazy(() => import('./components/ResetPassword'));

// ─── Full-screen loading fallback ─────────────────────────────────────────────
function PageLoader() {
  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: '3px solid #e5e7eb',
        borderTopColor: '#3b82f6',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Protected route (must be logged in) ─────────────────────────────────────
// If token is missing or expired → clear session and redirect to login.
function PrivateRoute() {
  if (hasValidSession()) return <Outlet />;
  clearSession();
  return <Navigate to="/" replace />;
}

// ─── Guest route (login page) ─────────────────────────────────────────────────
// If already logged in → redirect to dashboard.
// Otherwise → show login.
function GuestRoute() {
  return hasValidSession() ? <Navigate to="/dashboard" replace /> : <Outlet />;
}

// ─── App ──────────────────────────────────────────────────────────────────────
function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>

          {/* Public routes — no login needed */}
          <Route path="/verify/:token" element={<StartVerificationForm />} />
          <Route path="/verify/:token/form2" element={<RequestPermissionForm />} /> 
          <Route path="/verify/:token/form1" element={<VerificationForm />} />
          <Route path="/expired" element={<Expired />} />

          {/* Reset Password */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />  

          {/* Guest-only route — redirect to /dashboard if already logged in */}
          <Route element={<GuestRoute />}>
            <Route path="/" element={<LoginPage />} />
          </Route>

          {/* Protected routes — redirect to / if not logged in */}
          <Route element={<PrivateRoute />}>
            <Route path="/dashboard" element={<Dashboard />} />
            {/* Send Link is a drawer on the dashboard now; old links land there. */}
            <Route path="/send-sms"  element={<Navigate to="/dashboard" replace />} />
          </Route>

          {/* Fallback — unknown paths */}
          <Route path="*" element={<Navigate to="/" replace />} />

        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;